import moment from "moment";
import Grid from "@mui/material/Grid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IOrganizationUserProfile, IUserProfile, useCommonComponentContext } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Screens from "constants/screens";
import Strings from "constants/strings";
import Constants, { USER_ROLE } from "constants/index";
import TagService from "services/medkit/tag.service";
import UserService from "services/identity/user.service";
import ReportService from "services/medkit/report.service";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { setDataAlert, showLoading } from "store/slice/common.slice";
import { ActionRequest, BooleanInt, Mode, RepeatIssue, ReportDocumentType, ReportStatus } from "constants/enum";
import { ICodename, IDataDetailReport, IErrorReport, IFileUploaderRes, IRecordTag, IReportDocument, IReportTag, IRequestReportCreateUpdate } from "commons/interfaces";

import CardDescription from "./CardDescription";
import CardPhotoReport from "./CardPhotoReport";
import CardGeneralInfo from "./CardGeneralInfo";
import CardPatientInfo from "./CardPatientInfo";
import CardNotification from "./CardNotification";
import CardReporterInfo from "./CardReporterInfo";
import useDataCommon from "hooks/useDataCommon.hook";
import CardClassifyByHospital from "./CardClassifyByHospital";

const ReportType = {
    VOLUNTEER: "0",
    OBLIGATION: "1",
};

const tagService = new TagService();
const userService = new UserService();
const reportService = new ReportService();

const ReportIncidentScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const { getAllDepartments } = useDataCommon();
    const { tenantCode, serviceCode, clientId, userInfo } = useCommonComponentContext();
    const dataMapCodenames = useSelector((state: RootState) => state.common.codenames);
    const listPathName = useSelector((state: RootState) => state.titleRoute.listPathName);

    const [mode, setMode] = useState(Mode.View);
    const [hasUpdated, setHasUpdated] = useState(false);
    const [errorReport, setErrorReport] = useState<IErrorReport>({});
    const [dataReport, setDataReport] = useState<IDataDetailReport>({});
    const [dataOptionTags, setDataOptionTags] = useState<ICodename[]>([]);
    const [dataOptionGroups, setDataOptionGroups] = useState<ICodename[]>([]);

    // Report create user
    const [createUser, setCreateUser] = useState<{ id: string, fullName: string; email: string; phoneNumber: string }>();

    // Lưu backup data report
    const dataReportBackup = useRef<IDataDetailReport>({});
    const resetReporter = useCallback((overrideCreateUser?: typeof createUser) => {
        setDataReport(prev => {
            let newState = {
                ...prev,
                detector: overrideCreateUser?.fullName ?? (dataReportBackup.current?.detector || createUser?.fullName),
                reporter: overrideCreateUser?.fullName ?? (dataReportBackup.current?.reporter || createUser?.fullName),
                reporterId: overrideCreateUser?.id ?? userInfo?.userProfile?.identityId,
                reporterEmail: overrideCreateUser?.email ?? (dataReportBackup.current?.reporterEmail || createUser?.email),
                reporterPhone: overrideCreateUser?.phoneNumber ?? (dataReportBackup.current?.reporterPhone || createUser?.phoneNumber),

                titleNote: dataReportBackup.current?.titleNote,
                detectorTitle: dataReportBackup.current?.detectorTitle,
    
                witnessName1: dataReportBackup.current?.witnessName1,
                witnessName2: dataReportBackup.current?.witnessName2,
            };

            return newState;
        });
    }, [createUser, dataReport?.id])

    useEffect(() => {
        if (dataReport.anonymousReporter === BooleanInt.False) {
            dataReportBackup.current = JSON.parse(JSON.stringify(dataReport));
        }
    }, [dataReport])

    const [userRole, setUserRole] = useState("");
    const [dataMapReportTag, setDataMapReportTag] = useState<Map<string, IReportTag>>(new Map()); // tagId
    const [dataMapReportDocument, setDataMapReportDocument] = useState<Map<string, IReportDocument>>(new Map()); // id

    const reportId = searchParams.get("id");

    // Init
    useEffect(() => {
        if (Helpers.isNullOrEmpty(dataMapCodenames) || Object.keys(dataMapCodenames).length === 0)
            return;
        
        (async () => {
            try {
                dispatch(showLoading(true));

                setErrorReport({});
                setHasUpdated(false);

                if (userInfo?.userProfile?.roleCode) {
                    setUserRole(userInfo?.userProfile?.roleCode);
                };

                await getDataTags();

                const resultGroups = await getAllDepartments({
                    clientId: clientId,
                    organizationId: userInfo?.userProfile?.organizationId,
                });
                setDataOptionGroups(resultGroups);

                let report: IDataDetailReport = {};
                if (!Helpers.isNullOrEmpty(reportId)) {
                    let reporter = "";
                    const typeGroup: ICodename[] = dataMapCodenames?.reportType ?? [];
                    const objectGroup: ICodename[] = dataMapCodenames?.incidentObject ?? [];

                    const result = await reportService.detail(reportId);

                    if (result.anonymousReporter === BooleanInt.False) {
                        const reporterId = (!Helpers.isNullOrEmpty(result.reporter) && result.reporter !== "0") ? result.reporter : result.createUser;
                        const userProfile = await getUserDetail(reporterId, result.organizationId);
                        reporter = userProfile?.fullName;
                    };

                    const fileUrls: IFileUploaderRes[] = [];
                    const dataMapDocument: Map<string, IReportDocument> = new Map();
                    if (!Helpers.isNullOrEmpty(result.reportDocuments)) {
                        for (const element of [...result.reportDocuments || []]) {
                            if (element.type === ReportDocumentType.ImgReport) {
                                fileUrls.push({
                                    id: element.id,
                                    fileId: element.documentId,
                                    fileAccessUrl: element.documentUrl,
                                });
                                dataMapDocument.set(element.id, {
                                    id: element.id,
                                    type: element.type,
                                    action: ActionRequest.Update,
                                    documentId: element.documentId,
                                    documentUrl: element.documentUrl,
                                });
                            }
                        }
                    };

                    const idTags: string[] = [];
                    const dataMapTags: Map<string, IReportTag> = new Map();
                    if (!Helpers.isNullOrEmpty(result.reportTags)) {
                        for (const element of [...result.reportTags || []]) {
                            idTags.push(element.tagId);
                            dataMapTags.set(element.tagId, {
                                id: element.id,
                                tagId: element.tagId,
                                action: ActionRequest.Update,
                            });
                        }
                    };

                    if (result.reportStatus === ReportStatus.closed) {
                        const canEdit = ((Constants.PERMISSION as any)[`${userInfo?.userProfile?.roleCode || USER_ROLE.ADMIN}`].canEdit);
                        setMode(canEdit ? Mode.Update : Mode.View);
                    } else {
                        setMode(Mode.Update);
                    }

                    const selectedType = Object.keys(typeGroup as any)[+result.reportType];
                    const selectedObjects = Object.keys(objectGroup as any)[+result.incidentObject];
                    const createTime = !Helpers.isNullOrEmpty(result.createTime) && (result.createTime !== "0") && (result.createTime !== 0) ? result.createTime : undefined;
                    const reportTime = !Helpers.isNullOrEmpty(result.reportTime) && (result.reportTime !== "0") && (result.reportTime !== 0) ? result.reportTime : undefined;

                    setDataMapReportTag(dataMapTags);
                    setDataMapReportDocument(dataMapDocument);

                    report = {
                        ...result,
                        reporter,
                        createTime,
                        reportTime,
                        selectedType,
                        selectedObjects,
                        photoUrls: fileUrls,
                        tagIds: idTags,
                    };
                } else {
                    setMode(Mode.Create);

                    report = {
                        tenantCode: tenantCode,
                        serviceCode: serviceCode,
                        organizationId: userInfo?.userProfile?.organizationId,

                        anonymousReporter: BooleanInt.False,
                        reportTime: moment().unix().toString(),
                        createTime: moment().unix().toString(),
                        createUser: userInfo?.userProfile?.identityId,

                        detector: userInfo?.userProfile?.fullName,
                        reporter: userInfo?.userProfile?.fullName,
                        reporterId: userInfo?.userProfile?.identityId,
                        reporterEmail: userInfo?.userProfile?.email,
                        reporterPhone: userInfo?.userProfile?.phoneNumber,
                        incidentPlace: userInfo?.userProfile?.groupId,

                        reportedToPatien: "0",
                        reportedToDocument: "0",
                        reportedToRelative: "0",
                        firstClassification: "0",
                        firstAffectValuation: "0",
                        reportedToPersonInCharge: "0",

                        objectGender: Number([...dataMapCodenames?.gender || []]?.[0]?.code),
                        selectedType: [...dataMapCodenames?.reportType || []]?.[0]?.code as string,
                        selectedObjects: [...dataMapCodenames?.incidentObject || []]?.[0]?.code as string,
                    };
                };

                dataReportBackup.current = JSON.parse(JSON.stringify(report));
                getCreateUser(report);
                setDataReport(report);
            } catch (error) {
                const err = Helpers.handleExceptionError(error);
                !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
            } finally {
                dispatch(showLoading(false));
            }
        })();
    }, [reportId, dataMapCodenames, userInfo?.userProfile?.organizationId]);

    // Xử lý khi chuyển từ ẩn danh TỰ NGUYỆN sang BẮT BUỘC
    useEffect(() => {
        if (dataReport && dataReport.selectedType === ReportType.OBLIGATION && dataReport.anonymousReporter === BooleanInt.True) {
            const loggedInUser = {
                id: userInfo?.userProfile?.identityId,
                fullName: userInfo?.userProfile?.fullName,
                email: userInfo?.userProfile?.email,
                phoneNumber: userInfo?.userProfile?.phoneNumber,
            };
            resetReporter(loggedInUser);
            handleChangeValue("anonymousReporter", BooleanInt.False);
        }
    }, [dataReport?.selectedType, dataReport?.anonymousReporter])

    const getCreateUser = async (dataReport?: IDataDetailReport) => {
        let id = dataReport?.createUser;
        let organizationId = dataReport?.organizationId;

        if ((!Helpers.isNullOrEmpty(id) || id !== "0") &&
            (!Helpers.isNullOrEmpty(organizationId) || organizationId !== "0"))
        {
            id = dataReport?.createUser;
            organizationId = dataReport?.organizationId;
        } else {
            id = userInfo?.userProfile?.identityId;
            organizationId = userInfo?.userProfile?.organizationId;
        }
        
        const organizationUserProfile = await getUserDetail(id, organizationId);
        setCreateUser(organizationUserProfile);
    }

    const getUserDetail = async (id: string, organizationId?: string): Promise<typeof createUser> => {
        if (Helpers.isNullOrEmpty(id)) return null;

        try {
            const result = await userService.getDetail({ id, organizationId });
            const lastName = result?.organizationUserProfile?.lastName || "";
            const firstName = result?.organizationUserProfile?.firstName || "";
            const fullName = lastName + " " + firstName;
            
            const userProfile: typeof createUser = {
                fullName,
                id: result.id,
                email: result.organizationUserProfile.email || result.email,
                phoneNumber: result.organizationUserProfile.phoneNumber || result.phoneNumber,
            };
            
            return userProfile;
        } catch (error) {
            return null;;
        }
    };

    const getDataTags = async () => {
        try {
            const items: ICodename[] = [];
            const result = await tagService.getAll();
            for (const element of [...result || []]) {
                items.push({
                    code: element.id,
                    name: element.tagName,
                });
            };
            setDataOptionTags(items);
        } catch (error) {
        }
    };

    const handleCallBackModalAddTag = (item: IRecordTag) => {
        const val = [...dataReport?.tagIds || []];

        if (item.id) {
            val.push(item.id);
            setDataOptionTags(prev => [...prev || [], { code: item.id, name: item.tagName }]);
        };

        handleChangeValue("tagIds", val);
    };

    const handleChangeValue = async (key: string, value: any) => {
        if (key) {
            setHasUpdated(true);
            if (key === "incidentPlace") {
                if (Helpers.isString(value)) {
                    setDataReport(prev => ({
                        ...prev,
                        incidentPlace: value,
                    }));
                    setErrorReport(prev => ({
                        ...prev,
                        incidentPlace: undefined,
                    }));
                } else {
                    setDataReport(prev => ({
                        ...prev,
                        incidentPlace: value?.code,
                        incidentPlaceName: value?.name,
                    }));
                    setErrorReport(prev => ({
                        ...prev,
                        incidentPlace: undefined,
                        incidentPlaceName: undefined,
                    }));
                }
            } else if (key === "objectDepartment") {
                if (Helpers.isString(value)) {
                    setDataReport(prev => ({
                        ...prev,
                        objectDepartment: value,
                    }));
                    setErrorReport(prev => ({
                        ...prev,
                        objectDepartment: undefined,
                    }));
                } else {
                    setDataReport(prev => ({
                        ...prev,
                        objectDepartment: value?.code,
                        objectDepartmentName: value?.name,
                    }));

                    setErrorReport(prev => ({
                        ...prev,
                        objectDepartment: undefined,
                        objectDepartmentName: undefined,
                    }));
                }
            } else if (key === "referenceId") {
                setDataReport(prev => ({
                    ...prev,
                    referenceId: value?.code,
                    referenceCode: value?.name,
                }));

                setErrorReport(prev => ({
                    ...prev,
                    referenceId: undefined,
                    referenceCode: undefined,
                }));
            } else if (key === "repeatIssue") {
                setDataReport(prev => ({
                    ...prev,
                    [key]: value,
                    referenceId: undefined,
                    referenceCode: undefined,
                }));

                setErrorReport(prev => ({
                    ...prev,
                    [key]: undefined,
                    referenceId: undefined,
                    referenceCode: undefined,
                }));
            } else {
                setDataReport(prev => ({
                    ...prev,
                    [key]: value,
                }));

                setErrorReport(prev => ({
                    ...prev,
                    [key]: undefined,
                }));
            }
        }
    };

    const handleChangeMultiValue = (data: IDataDetailReport) => {
        const keys = Object.keys(data).filter(key => {
            const obj = JSON.parse(JSON.stringify(data)) as {[key: string]: any};
            return !Helpers.isNullOrEmpty(obj[key]);
        });

        setDataReport(prev => ({ ...prev, ...data }));

        setErrorReport(prev => {
            let obj = JSON.parse(JSON.stringify(prev)) as {[key: string]: any};
            for (const key of keys) {
                obj[key] = undefined;
            }
            return obj;
        });
    }

    const handleValidate = () => {
        let isValidate = true;
        if (Helpers.isNullOrEmpty(dataReport?.shortDescription)) {
            isValidate = false;
            setErrorReport(prev => ({
                ...prev,
                shortDescription: Strings.REPORT.PLEASE_ENTER_SHORT_DESCRIPTION,
            }));
        }
        if (
            (dataReport?.repeatIssue === RepeatIssue.RepeatedUnconfirmed) &&
            Helpers.isNullOrEmpty(dataReport?.referenceId)
        ) {
            isValidate = false;
            setErrorReport(prev => ({
                ...prev,
                referenceId: Strings.REPORT.PLEASE_SELECT_REFERENCE_INCIDENT,
            }));
        }
        return isValidate;
    };

    const handleSubmit = async () => {
        if (!handleValidate()) {
            return;
        } else {
            try {

                dispatch(showLoading(true));

                // Related photos
                let detailDescription = "";
                if (!Helpers.isNullOrEmpty(dataReport?.shortDescription)) {
                    detailDescription += dataReport?.shortDescription;
                }
                if (!Helpers.isNullOrEmpty(dataReport?.firstSuggestedSolution)) {
                    detailDescription += `\n${dataReport?.firstSuggestedSolution}`;
                }

                let appliedTreatment = "";
                if (!Helpers.isNullOrEmpty(dataReport?.firstTreatment)) {
                    appliedTreatment += `${dataReport?.firstTreatment}`;
                }

                let recoverAction = "";
                if (!Helpers.isNullOrEmpty(appliedTreatment)) {
                    recoverAction = `${appliedTreatment}`;
                }

                const objectGender = dataReport?.objectGender || Number([...dataMapCodenames?.gender || []]?.[0]?.code);
                const reportType = dataReport?.selectedType || [...dataMapCodenames?.reportType || []]?.[0]?.code as string;
                const incidentObject = dataReport?.selectedObjects || [...dataMapCodenames?.incidentObject || []]?.[0]?.code as string;

                const idsPhoto = [...dataReport?.photoUrls || []].map(e => e.id);
                const newReportDocuments: IReportDocument[] = [];
                for (const id of Array.from(dataMapReportDocument.keys())) {
                    if (!idsPhoto.includes(id)) {
                        const itemGet = dataMapReportDocument.get(id);
                        itemGet && newReportDocuments.push({ ...itemGet, action: ActionRequest.Delete });
                    }
                };
                for (const element of [...dataReport?.photoUrls || []]) {
                    if (element.fileId && element.fileAccessUrl) {
                        const action = element.id ? ActionRequest.Update : ActionRequest.Create;
                        newReportDocuments.push({
                            id: element.id,
                            action: action,
                            documentId: element.fileId,
                            documentUrl: element.fileAccessUrl,
                            type: ReportDocumentType.ImgReport,
                        });
                    }
                };

                const newReportTags: IReportTag[] = [];
                for (const tid of Array.from(dataMapReportTag.keys())) {
                    if (![...dataReport?.tagIds || []].includes(tid)) {
                        const itemGet = dataMapReportTag.get(tid);
                        itemGet && newReportTags.push({ ...itemGet, action: ActionRequest.Delete });
                    }
                };
                for (const str of [...dataReport?.tagIds || []]) {
                    const itemGet = dataMapReportTag.get(str);
                    const action = !Helpers.isNullOrEmpty(itemGet) ? ActionRequest.Update : ActionRequest.Create;
                    newReportTags.push({
                        id: itemGet?.id,
                        tagId: str,
                        action: action,
                    });
                };

                const requestBody: IRequestReportCreateUpdate = {
                    tenantCode: dataReport?.tenantCode,
                    serviceCode: dataReport?.serviceCode,
                    organizationId: dataReport?.organizationId,

                    id: dataReport?.id,
                    reportType: reportType,
                    reportTime: dataReport?.reportTime,
                    incidentObject: incidentObject,

                    // Object information,
                    objectName: dataReport?.objectName,
                    objectGender: objectGender,
                    objectBirthday: dataReport?.objectBirthday,
                    objectDepartment: dataReport?.objectDepartment,
                    objectMedicalRecordNo: dataReport?.objectMedicalRecordNo,

                    // Incident place,
                    detailPlace: dataReport?.detailPlace,
                    incidentPlace: !Helpers.isNullOrEmpty(dataReport?.incidentPlace) ? dataReport?.incidentPlace : undefined,
                    firstTreatment: dataReport?.firstTreatment,
                    shortDescription: dataReport?.shortDescription || "",
                    firstSuggestedSolution: dataReport?.firstSuggestedSolution,

                    // By hospital
                    byHospital: dataReport?.byHospital,
                    byHospitalSource: dataReport?.byHospitalSource,

                    // Notification
                    reportedToPatien: dataReport?.reportedToPatien || "0",
                    reportedToDocument: dataReport?.reportedToDocument || "0",
                    reportedToRelative: dataReport?.reportedToRelative || "0",
                    firstClassification: dataReport?.firstClassification || "0",
                    firstAffectValuation: dataReport?.firstAffectValuation || "0",
                    reportedToPersonInCharge: dataReport?.reportedToPersonInCharge || "0",

                    // Reporter
                    // reporter: dataReport?.reporterId,
                    anonymousReporter: dataReport.anonymousReporter,
                    reporterPhone: dataReport?.reporterPhone,
                    reporterEmail: dataReport?.reporterEmail,

                    // Detector
                    detector: dataReport?.detector,
                    titleNote: dataReport?.titleNote,
                    detectorTitle: dataReport?.detectorTitle,

                    // Witness
                    recoverAction,
                    appliedTreatment,
                    detailDescription,
                    witnessName1: dataReport?.witnessName1,
                    witnessName2: dataReport?.witnessName2,

                    referenceId: dataReport?.referenceId,
                    repeatIssue: dataReport?.repeatIssue,

                    reportTags: newReportTags,
                    reportDocuments: newReportDocuments,
                };

                if (!Helpers.isNullOrEmpty(dataReport?.id)) {
                    await reportService.update(requestBody);
                    dispatch(setDataAlert({ message: Strings.REPORT.UPDATE_REPORT_SUCCESS, type: "success" }));
                } else {
                    await reportService.create(requestBody);
                    dispatch(setDataAlert({ message: Strings.REPORT.CREATE_REPORT_SUCCESS, type: "success" }));
                }
                handleGoBack();
            } catch (error: any) {
                const err = Helpers.handleExceptionError(error);
                !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
            } finally {
                dispatch(showLoading(false));
            }
        }
    };

    const handleGoBack = () => {
        const arrPath = `${window.location.pathname}`.split("/");
        if (`/${arrPath[1]}` === (Screens.REPORT)) {
            const itemP = listPathName.find((el) => el.pathName === Screens.REPORT);
            navigate(Screens.REPORT + (itemP ? itemP.query : ""));
        } else if (`/${arrPath[1]}` === (Screens.MONITORING)) {
            const itemP = listPathName.find((el) => el.pathName === Screens.MONITORING);
            navigate(Screens.MONITORING + (itemP ? itemP.query : ""));
        } else {
            const itemP = listPathName.find((el) => el.pathName === Screens.REPORT);
            navigate(Screens.REPORT + (itemP ? itemP.query : ""));
        }
    };

    return (
        <DashboardLayout
            isPermission
            requiredOrganization
            title={Strings.REPORT.INCIDENT_REPORT}
            route={[
                { title: Strings.REPORT.LIST_REPORT, route: Screens.REPORT },
                { title: Strings.REPORT.INCIDENT_REPORT, route: "", },
            ]}
            listActions={[
                {
                    color: "secondary",
                    onPress: handleGoBack,
                    title: Strings.COMMON.BACK,
                },
                {
                    color: "dark",
                    onPress: handleSubmit,
                    title: Strings.COMMON.SAVE,
                },
            ]}
        >
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <CardGeneralInfo
                        mode={mode}
                        data={dataReport}
                        error={errorReport}
                        handleChangeValue={handleChangeValue}
                        onCallBackModalAddTag={handleCallBackModalAddTag}
                        dataOptions={{
                            tags: [...dataOptionTags || []],
                            reportType: [...dataMapCodenames?.reportType || []],
                            incidentObject: [...dataMapCodenames?.incidentObject || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardPatientInfo
                        mode={mode}
                        data={dataReport}
                        error={errorReport}
                        handleChangeValue={handleChangeValue}
                        dataOptions={{
                            groups: [...dataOptionGroups || []],
                            gender: [...dataMapCodenames?.gender || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardDescription
                        mode={mode}
                        data={dataReport}
                        error={errorReport}
                        handleChangeValue={handleChangeValue}
                        dataOptionGroups={[...dataOptionGroups || []]}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardClassifyByHospital
                        mode={mode}
                        data={dataReport}
                        error={errorReport}
                        handleChangeValue={handleChangeValue}
                        dataOptions={{
                            byHospital: [...dataMapCodenames?.byHospital || []],
                            byHospitalSource: [...dataMapCodenames?.byHospitalSource || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardNotification
                        mode={mode}
                        data={dataReport}
                        error={errorReport}
                        handleChangeValue={handleChangeValue}
                        dataOptions={{
                            level: [...dataMapCodenames?.level || []],
                            common: [...dataMapCodenames?.common || []],
                            classtify: [...dataMapCodenames?.classtify || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardReporterInfo
                        mode={mode}
                        data={dataReport}
                        error={errorReport}
                        handleChangeValue={handleChangeValue}
                        handleChangeMultiValue={handleChangeMultiValue}
                        resetReporter={resetReporter}
                        dataOptions={{
                            staffTitle: [...dataMapCodenames?.staffTitle || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardPhotoReport
                        mode={mode}
                        data={dataReport}
                        handleChangeValue={handleChangeValue}
                    />
                </Grid>
            </Grid>
        </DashboardLayout>
    );
};

export default ReportIncidentScreen;
